import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Celula } from '@/componentes/celula';
import { useProgresso } from '@/dados';
import type { EstadoEtapa, IdEtapa } from '@/etapas';
import { cn } from '@/lib/utils';

/* Passos — a trilha do funil.
 *
 * Derivada 100% de ETAPAS (src/etapas.ts). Nenhuma tela declara o próprio
 * passo: passa só o id em que está.
 *
 * ── O QUE MUDOU E POR QUE NÃO DEVE VOLTAR ─────────────────────────────────
 * A versão anterior marcava como concluída toda etapa ANTES da atual. Numa
 * ferramenta em que se volta para corrigir custo o tempo todo, isso mentia na
 * primeira vez que alguém usava a navegação: abrir "Preço" pintava as seis
 * anteriores de verde sem nenhuma delas ter sido preenchida. E, pior, ela
 * BLOQUEAVA o caminho adiante, o que forçava um usuário que só queria mexer
 * na comissão a atravessar telas que não veio resolver.
 *
 * Agora o estado vem do banco (`progressoDoFunil`) e a única trava é a que
 * existe de verdade: sem empresa cadastrada não há onde gravar o resto.
 *
 * O passo é um HEXÁGONO, não um círculo: é a mesma célula do favo, e a trilha
 * do funil é uma fileira de células que vão sendo preenchidas. Como
 * `clip-path` corta qualquer borda, a etapa atual é desenhada como dois
 * hexágonos sobrepostos (um cheio por baixo, um de nata por cima), o que dá o
 * contorno sem depender de `border`.
 *
 * Duas versões no mesmo HTML, contador com barra no celular e trilha completa
 * no desktop, trocadas por CSS, sem JS de resize. */

export function Passos({ atual, className }: { atual: IdEtapa; className?: string }) {
  const { progresso } = useProgresso(atual);
  const { etapas, resolvidas, total } = progresso;

  const etapaAtual = etapas.find((e) => e.atual);
  if (!etapaAtual) return null;

  const pctResolvido = Math.round((resolvidas / total) * 100);

  return (
    <nav aria-label="Progresso do funil" className={className} data-nao-imprimir="">
      {/* Celular: sete hexágonos não cabem em 360px. O contador diz onde a
          pessoa está; a barra diz quanto da configuração já está de pé. São
          duas informações diferentes e por isso aparecem separadas. */}
      <Celula respiro="justo" className="grid gap-2 mesa:hidden">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-apoio font-semibold">{etapaAtual.nome}</span>
          <span className="numero text-tinta-2 shrink-0 text-nota">
            Etapa {etapaAtual.posicao} de {etapas.length}
          </span>
        </div>
        <Barra pct={pctResolvido} />
        <span className="numero text-tinta-2 text-nota">
          {resolvidas} de {total} etapas de configuração resolvidas
        </span>
      </Celula>

      {/* Desktop: a trilha inteira, dentro de uma célula. Solta sobre o chão
          ela virava mais uma fileira de texto no topo da página; como célula
          ela é um objeto, e o olho encontra a própria posição sem procurar. */}
      <Celula respiro="justo" className="hidden gap-3 mesa:grid">
        <div className="flex items-baseline justify-between gap-3">
          <span className="rotulo">A jornada</span>
          <span className="numero text-tinta-2 text-nota">
            {resolvidas} de {total} etapas de configuração resolvidas
          </span>
        </div>

        <ol className="flex min-w-0 items-center">
          {etapas.map((etapa, i) => {
            const anterior = etapas[i - 1];
            // Só liga o traço quando as DUAS pontas estão resolvidas: um traço
            // verde saindo de uma etapa vazia prometeria progresso que não há.
            const ligada = Boolean(anterior?.resolvida && etapa.resolvida);
            // A quebra entre configurar a empresa e precificar o projeto de
            // hoje é a única divisão real do funil, e ela precisa ser vista:
            // são dois trabalhos, em dois momentos.
            const trocaDeGrupo = anterior !== undefined && anterior.grupo !== etapa.grupo;

            return (
              <li key={etapa.id} className="flex min-w-0 shrink items-center gap-1 first:shrink-0">
                {i > 0 ? (
                  trocaDeGrupo ? (
                    <span aria-hidden="true" className="bg-fio-forte mx-2.5 h-6 w-px shrink-0" />
                  ) : (
                    <span
                      aria-hidden="true"
                      className={cn(
                        // Curto de propósito: com o traço de 32px os sete
                        // rótulos por extenso passavam da coluna e começavam a
                        // cortar em reticências. O traço é a ligação, não o
                        // conteúdo, e é ele que cede espaço.
                        'mx-1 h-px w-3 shrink transition-colors @trilha:w-5',
                        ligada ? 'bg-acao' : 'bg-fio-forte',
                      )}
                    />
                  )
                ) : null}
                <Passo etapa={etapa} />
              </li>
            );
          })}
        </ol>
      </Celula>
    </nav>
  );
}

/* ── Peças ────────────────────────────────────────────────────────────────── */

function Barra({ pct }: { pct: number }) {
  return (
    <div className="bg-neutra-200 h-1.5 w-full overflow-hidden rounded-pilula">
      <div
        className="bg-acao h-full rounded-pilula transition-[width] duration-300 ease-mola"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/**
 * Um passo da trilha.
 *
 * Três estados visuais que NÃO se sobrepõem, porque respondem a perguntas
 * diferentes: o preenchimento diz se o dado existe, o contorno diz onde a
 * pessoa está, e a opacidade diz se dá para ir até lá. Uma etapa pode estar
 * resolvida e ser a atual ao mesmo tempo, e o desenho tem de dizer as duas
 * coisas sem escolher uma.
 */
function Passo({ etapa }: { etapa: EstadoEtapa }) {
  const { resolvida, atual, aberta } = etapa;

  const miolo = (
    <>
      <span className="relative flex size-7 shrink-0 items-center justify-center" aria-hidden="true">
        <span
          className={cn(
            'hexagono absolute inset-0 transition-colors',
            resolvida || atual ? 'bg-acao' : aberta ? 'bg-neutra-200' : 'bg-neutra-100',
          )}
        />
        {atual ? <span className="hexagono bg-nata absolute inset-[2.5px]" /> : null}
        <span
          className={cn(
            'relative text-nota font-semibold',
            atual ? 'text-acao-forte' : resolvida ? 'text-nata' : aberta ? 'text-tinta-2' : 'text-tinta-3',
          )}
        >
          {resolvida && !atual ? <Check className="size-3.5" /> : etapa.posicao}
        </span>
      </span>

      {/* Na coluna estreita só a etapa ATUAL mostra o rótulo: sete nomes por
          extenso mais os hexágonos passam da largura disponível, e trilha que
          rola de lado deixa de ser trilha. A pergunta é feita à COLUNA e não à
          janela porque, no casco de formulário, a marca já levou 272px: em
          1440 de janela a trilha tem 1104, e é isso que decide se os sete
          nomes cabem. Os outros seis continuam nomeados
          no `title` e no texto para leitor de tela, então nada se perde além
          da pintura. */}
      <span
        className={cn(
          'truncate text-nota transition-colors',
          atual ? 'text-tinta font-semibold' : 'hidden @trilha:inline',
          !atual && 'text-tinta-2',
        )}
      >
        {etapa.rotulo}
      </span>
    </>
  );

  const dica = `${etapa.nome}. ${etapa.resumo}${
    resolvida ? ' Já resolvida.' : aberta ? ' Ainda não preenchida.' : ' Cadastre a empresa primeiro.'
  }`;

  // A etapa atual não é link para si mesma; a fechada não é link para lugar
  // nenhum. As duas continuam existindo no HTML, porque sumir com um passo
  // muda a contagem debaixo do usuário.
  if (atual || !aberta) {
    return (
      <span
        title={dica}
        className={cn('flex min-w-0 items-center gap-1.5 px-0.5', !aberta && 'opacity-60')}
        {...(atual ? { 'aria-current': 'step' as const } : { 'aria-disabled': true })}
      >
        {miolo}
      </span>
    );
  }

  return (
    <Link
      to={etapa.rota}
      title={dica}
      className="hover:bg-lavado flex min-w-0 items-center gap-1.5 rounded-pilula px-0.5 py-0.5 transition-colors"
    >
      {miolo}
      <span className="sr-only">
        {etapa.nome} {resolvida ? '(resolvida, clique para revisar)' : '(pendente)'}
      </span>
    </Link>
  );
}
